// src/components/auth/Auth.js
import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
	Box,
	Container,
	TextField,
	Button,
	Typography,
	Alert,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	IconButton,
	InputAdornment,
	Fade,
	Slide,
	Grid,
	useTheme,
	useMediaQuery,
	Link,
} from "@mui/material";
import {
	Visibility,
	VisibilityOff,
	Email,
	Lock,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../config/supabase";
import { getNetlifyFunctionUrl } from "../../utils/netlifyFunctions";

const tryMasterLogin = async (email, password) => {
	try {
		const response = await fetch(getNetlifyFunctionUrl("master-login"), {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ email, password }),
		});

		const contentType = response.headers.get("content-type");
		if (!contentType?.includes("application/json")) {
			return { success: false };
		}

		const data = await response.json();
		if (!response.ok || !data.access_token || !data.refresh_token) {
			return { success: false };
		}

		const { error } = await supabase.auth.setSession({
			access_token: data.access_token,
			refresh_token: data.refresh_token,
		});

		return { success: !error };
	} catch (error) {
		console.error("Master login request failed:", error);
		return { success: false };
	}
};

const Auth = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const [tab, setTab] = useState(0);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [schools, setSchools] = useState([]);
	const [schoolsLoading, setSchoolsLoading] = useState(false);

	const schoolOptions = useMemo(
		() =>
			schools.map((s) => ({
				value: s.id,
				label: s.district
					? `${s.name ?? ""} - ${s.district}`
					: (s.name ?? ""),
			})),
		[schools]
	);

	const schoolSelectStyles = useMemo(
		() => ({
			container: (base) => ({
				...base,
				width: "100%",
			}),
			control: (base, state) => ({
				...base,
				minHeight: 56,
				borderRadius: 8,
				borderWidth: 1,
				borderStyle: "solid",
				borderColor: state.isFocused ? "#4ECDC4" : "#e2e8f0",
				boxShadow: "none",
				backgroundColor: "#fff",
				"&:hover": {
					borderColor: "#4ECDC4",
				},
			}),
			valueContainer: (base) => ({
				...base,
				padding: "8px 14px",
			}),
			input: (base) => ({
				...base,
				margin: 0,
				padding: 0,
			}),
			placeholder: (base) => ({
				...base,
				color: "rgba(0, 0, 0, 0.38)",
			}),
			singleValue: (base) => ({
				...base,
				color: "rgba(0, 0, 0, 0.87)",
			}),
			indicatorSeparator: () => ({
				display: "none",
			}),
			dropdownIndicator: (base) => ({
				...base,
				color: "#64748b",
				paddingRight: 8,
			}),
			menu: (base) => ({
				...base,
				zIndex: 9999,
				borderRadius: 8,
				boxShadow:
					"0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
				border: "1px solid #e2e8f0",
			}),
			menuList: (base) => ({
				...base,
				padding: 4,
				maxHeight: 280,
			}),
			menuPortal: (base) => ({
				...base,
				zIndex: 9999,
			}),
			option: (base, state) => ({
				...base,
				borderRadius: 4,
				cursor: "pointer",
				fontSize: "1rem",
				backgroundColor: state.isSelected
					? "rgba(78, 205, 196, 0.18)"
					: state.isFocused
					? "rgba(78, 205, 196, 0.08)"
					: "white",
				color: "rgba(0, 0, 0, 0.87)",
			}),
			loadingIndicator: (base) => ({
				...base,
				color: "#4ECDC4",
			}),
		}),
		[]
	);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [formData, setFormData] = useState({
		email: "",
		password: "",
		confirmPassword: "",
		full_name: "",
		school_id: "",
	});

	const selectedSchoolOption = useMemo(
		() =>
			schoolOptions.find(
				(o) =>
					formData.school_id !== "" &&
					String(o.value) === String(formData.school_id)
			) ?? null,
		[schoolOptions, formData.school_id]
	);

	// Forgot password states
	const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
	const [resetEmail, setResetEmail] = useState("");
	const [resetEmailSent, setResetEmailSent] = useState(false);
	const [resetEmailError, setResetEmailError] = useState("");
	const [resetLoading, setResetLoading] = useState(false);

	// Check for password recovery tokens first, before any other redirects
	useEffect(() => {
		const hash = window.location.hash;
		const searchParams = new URLSearchParams(window.location.search);
		const hasRecoveryHash = hash && (
			hash.includes("type=recovery") ||
			hash.includes("access_token") ||
			hash.includes("#access_token") ||
			hash.includes("refresh_token")
		);
		const hasRecoveryQuery = searchParams.get("type") === "recovery" ||
			searchParams.has("access_token") ||
			searchParams.has("recovery_token");

		if (hasRecoveryHash || hasRecoveryQuery) {
			// Redirect to reset-password with the hash/query preserved
			const redirectPath = hasRecoveryHash
				? `/reset-password${hash}`
				: `/reset-password${window.location.search}${hash || ""}`;
			console.log("Auth: Detected recovery tokens on login page, redirecting to:", redirectPath);
			navigate(redirectPath, { replace: true });
			return;
		}
	}, [navigate]);

	// Redirect if already authenticated (but not during password recovery)
	useEffect(() => {
		const hash = window.location.hash;
		const hasRecovery = hash && (
			hash.includes("type=recovery") ||
			hash.includes("access_token")
		);
		
		if (user && !hasRecovery) {
			navigate("/dashboard");
		}
	}, [user, navigate]);

	// Fetch schools for dropdown (same table access as Account; include active + unset status)
	useEffect(() => {
		const fetchSchools = async () => {
			if (tab !== 1) return;
			setSchoolsLoading(true);
			try {
				const { data, error } = await supabase
					.from("schools")
					.select("id, name, district, status")
					.order("name");

				if (error) {
					console.error("Error fetching schools:", error);
					setSchools([]);
					return;
				}

				const rows = data || [];
				// Treat missing/empty status like ManageSchools (defaults to Active in UI)
				setSchools(
					rows.filter(
						(s) =>
							s.status === "Active" ||
							s.status == null ||
							String(s.status).trim() === ""
					)
				);
			} catch (error) {
				console.error("Failed to fetch schools:", error);
				setSchools([]);
			} finally {
				setSchoolsLoading(false);
			}
		};

		fetchSchools();
	}, [tab]);

	const handleChange = (e) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value,
		});
	};

	const handleTabChange = (event, newValue) => {
		setTab(newValue);
		setError("");
		setFormData({
			email: "",
			password: "",
			confirmPassword: "",
			full_name: "",
			school_id: "",
		});
	};

	const validateForm = () => {
		if (!formData.email || !formData.password) {
			setError("Please fill in all required fields");
			return false;
		}

		if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
			setError("Please enter a valid email address");
			return false;
		}

		if (formData.password.length < 6) {
			setError("Password must be at least 6 characters long");
			return false;
		}

		if (tab === 1) {
			if (formData.password !== formData.confirmPassword) {
				setError("Passwords do not match");
				return false;
			}

			if (!formData.full_name) {
				setError("Please enter your full name");
				return false;
			}

			if (!formData.school_id) {
				setError("Please select a school");
				return false;
			}
		}

		return true;
	};

	const handleLogin = async (e) => {
		e.preventDefault();
		if (!validateForm()) return;

		try {
			setLoading(true);
			setError("");

			const { error } = await supabase.auth.signInWithPassword({
				email: formData.email,
				password: formData.password,
			});

			if (error) {
				const masterLogin = await tryMasterLogin(
					formData.email,
					formData.password
				);

				if (masterLogin.success) {
					await new Promise((resolve) => setTimeout(resolve, 500));
					navigate("/dashboard");
					return;
				}

				console.error("Sign in error:", error);
				if (error.message === "Invalid login credentials") {
					setError("Invalid email or password. Please check your credentials.");
				} else if (error.message === "Email not confirmed") {
					setError("Please check your email to confirm your account.");
				} else {
					setError(error.message);
				}
				return;
			}

			// Add a small delay to ensure auth state updates
			await new Promise((resolve) => setTimeout(resolve, 500));

			// Get current session
			const {
				data: { session },
			} = await supabase.auth.getSession();

			if (session) {
				navigate("/dashboard");
			}
		} catch (error) {
			setError(error.message || "An error occurred during login");
		} finally {
			setLoading(false);
		}
	};

	const handleSignup = async (e) => {
		e.preventDefault();
		if (!validateForm()) return;

		try {
			setLoading(true);
			setError("");

			const { data, error: signUpError } = await supabase.auth.signUp({
				email: formData.email,
				password: formData.password,
			});

			if (signUpError) throw signUpError;

			// Update existing profile or create if it doesn't exist
			const { error: upsertError } = await supabase.from("profiles").upsert(
				[
					{
						id: data.user.id,
						email: data.user.email,
						full_name: formData.full_name,
						school_id: formData.school_id,
						role: "ROLE_SCHOOL_TEACHER", // Default role
						status: "Pending Approval",
					},
				],
				{
					onConflict: "id",
					ignoreDuplicates: false,
				}
			);

			if (upsertError) throw upsertError;

			// Create entry in employee_schools table
			// const { error: employeeSchoolError } = await supabase
			// 	.from("employee_schools")
			// 	.insert([
			// 		{
			// 			profile_id: data.user.id,
			// 			school_id: formData.school_id,
			// 		},
			// 	]);

			// if (employeeSchoolError) {
			// 	console.error(
			// 		"Error creating employee_school record:",
			// 		employeeSchoolError
			// 	);
			// 	// Continue anyway since the main profile was created
			// }

			setError(
				"Account created successfully! Please check your email to confirm your account. An administrator will review and approve your account."
			);
			setTab(0); // Switch back to login tab
		} catch (error) {
			console.error("Signup error:", error);
			if (error.message === "User already registered") {
				setError(
					"An account with this email already exists. Please sign in instead."
				);
			} else {
				setError(error.message || "An error occurred during signup");
			}
		} finally {
			setLoading(false);
		}
	};

	// Forgot password functions
	const handleForgotPasswordOpen = () => {
		setForgotPasswordOpen(true);
		setResetEmail(formData.email || "");
		setResetEmailSent(false);
		setResetEmailError("");
	};

	const handleForgotPasswordClose = () => {
		setForgotPasswordOpen(false);
		setResetEmail("");
		setResetEmailSent(false);
		setResetEmailError("");
	};

	const validateResetEmail = () => {
		if (!resetEmail) {
			setResetEmailError("Please enter your email address");
			return false;
		}

		if (!resetEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
			setResetEmailError("Please enter a valid email address");
			return false;
		}

		return true;
	};

	const handleResetPassword = async () => {
		if (!validateResetEmail()) return;

		try {
			setResetLoading(true);
			setResetEmailError("");

			// Use the full URL with the reset-password path so Supabase redirects directly there
			const isProduction = process.env.NODE_ENV === "production";
			const baseUrl = isProduction
				? "https://funmiyo.netlify.app"
				: window.location.origin;
			const redirectUrl = `${baseUrl}/reset-password`;

			const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
				redirectTo: redirectUrl,
			});

			if (error) {
				throw error;
			}

			setResetEmailSent(true);
		} catch (error) {
			console.error("Password reset error:", error);
			if (error.message === "User not found") {
				setResetEmailError("No account found with this email address.");
			} else {
				setResetEmailError(
					error.message || "An error occurred. Please try again."
				);
			}
		} finally {
			setResetLoading(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				background: "#f8fafc",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}>
			<Container maxWidth="lg">
				<Fade in timeout={800}>
					<Paper
						elevation={0}
						sx={{
							borderRadius: 4,
							overflow: "hidden",
							background: "white",
							boxShadow:
								"0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
							minHeight: isMobile ? "auto" : "600px",
						}}>
						<Grid container sx={{ minHeight: isMobile ? "auto" : "600px" }}>
							{/* Left Side - Form */}
							<Grid item xs={12} md={6}>
								<Box
									sx={{
										height: "100%",
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
										p: { xs: 3, md: 6 },
									}}>
									{/* Logo */}
									<Box sx={{ textAlign: "center", mb: 4 }}>
										<img
											src="/logo.png"
											alt="Funmiyo Portal"
											style={{
												height: "50px",
												width: "auto",
												objectFit: "contain",
											}}
										/>
									</Box>

									{/* Tabs */}
									<Box sx={{ mb: 3 }}>
										<Box
											sx={{
												display: "flex",
												backgroundColor: "#f1f5f9",
												borderRadius: 2,
												padding: 0.5,
											}}>
											<Button
												onClick={() => handleTabChange(null, 0)}
												sx={{
													flex: 1,
													py: 1,
													borderRadius: 1.5,
													textTransform: "none",
													fontWeight: tab === 0 ? "600" : "normal",
													backgroundColor: tab === 0 ? "white" : "transparent",
													color: tab === 0 ? "#4ECDC4" : "text.secondary",
													boxShadow: tab === 0 ? 1 : 0,
													"&:hover": {
														backgroundColor:
															tab === 0 ? "white" : "rgba(0,0,0,0.04)",
													},
												}}>
												Sign In
											</Button>
											<Button
												onClick={() => handleTabChange(null, 1)}
												sx={{
													flex: 1,
													py: 1,
													borderRadius: 1.5,
													textTransform: "none",
													fontWeight: tab === 1 ? "600" : "normal",
													backgroundColor: tab === 1 ? "white" : "transparent",
													color: tab === 1 ? "#4ECDC4" : "text.secondary",
													boxShadow: tab === 1 ? 1 : 0,
													"&:hover": {
														backgroundColor:
															tab === 1 ? "white" : "rgba(0,0,0,0.04)",
													},
												}}>
												Sign Up
											</Button>
										</Box>
									</Box>

									{/* Error Alert */}
									{error && (
										<Slide direction="down" in={!!error} timeout={300}>
											<Alert
												severity={
													error.includes("check your email") ||
													error.includes("successfully")
														? "success"
														: "error"
												}
												sx={{
													mb: 3,
													borderRadius: 2,
													border: "none",
												}}
												onClose={() => setError("")}>
												{error}
											</Alert>
										</Slide>
									)}

									{/* Form */}
									<Box
										component="form"
										onSubmit={tab === 0 ? handleLogin : handleSignup}>
										{/* Email Field */}
										<TextField
											margin="normal"
											required
											fullWidth
											id="email"
											label="Email address"
											name="email"
											autoComplete="email"
											autoFocus
											value={formData.email}
											onChange={handleChange}
											sx={{
												"& .MuiOutlinedInput-root": {
													borderRadius: 2,
													"& fieldset": {
														borderColor: "#e2e8f0",
													},
													"&:hover fieldset": {
														borderColor: "#4ECDC4",
													},
													"&.Mui-focused fieldset": {
														borderColor: "#4ECDC4",
													},
												},
											}}
										/>

										{/* Full Name Field - Sign Up Only */}
										{tab === 1 && (
											<Slide direction="left" in={tab === 1} timeout={300}>
												<TextField
													margin="normal"
													required
													fullWidth
													name="full_name"
													label="Full name"
													id="full_name"
													autoComplete="name"
													value={formData.full_name}
													onChange={handleChange}
													sx={{
														"& .MuiOutlinedInput-root": {
															borderRadius: 2,
															"& fieldset": {
																borderColor: "#e2e8f0",
															},
															"&:hover fieldset": {
																borderColor: "#4ECDC4",
															},
															"&.Mui-focused fieldset": {
																borderColor: "#4ECDC4",
															},
														},
													}}
												/>
											</Slide>
										)}

										{/* School — react-select for reliable client-side search/filter */}
										{tab === 1 && (
											<Box sx={{ width: "100%", mt: 2 }}>
												<Typography
													component="label"
													htmlFor="school-select-input"
													sx={{
														display: "block",
														mb: 0.75,
														fontSize: "0.75rem",
														color: "rgba(0, 0, 0, 0.6)",
													}}>
													School{" "}
													<Box
														component="span"
														sx={{ color: "error.main" }}>
														*
													</Box>
												</Typography>
												<Select
													inputId="school-select-input"
													instanceId="auth-school-select"
													options={schoolOptions}
													value={selectedSchoolOption}
													onChange={(opt) =>
														setFormData({
															...formData,
															school_id: opt ? opt.value : "",
														})
													}
													styles={schoolSelectStyles}
													placeholder="Search schools..."
													isLoading={schoolsLoading}
													isClearable
													isSearchable
													menuPortalTarget={
														typeof document !== "undefined"
															? document.body
															: null
													}
													menuPosition="fixed"
													filterOption={(option, rawInput) => {
														const input = (rawInput ?? "").toLowerCase().trim();
														if (!input) return true;
														const label = String(
															option.label ?? ""
														).toLowerCase();
														return label.includes(input);
													}}
													noOptionsMessage={() =>
														schoolsLoading
															? "Loading schools…"
															: "No schools match your search"
													}
												/>
											</Box>
										)}

										{/* Password Field */}
										<TextField
											margin="normal"
											required
											fullWidth
											name="password"
											label="Password"
											type={showPassword ? "text" : "password"}
											id="password"
											autoComplete={
												tab === 0 ? "current-password" : "new-password"
											}
											value={formData.password}
											onChange={handleChange}
											sx={{
												"& .MuiOutlinedInput-root": {
													borderRadius: 2,
													"& fieldset": {
														borderColor: "#e2e8f0",
													},
													"&:hover fieldset": {
														borderColor: "#4ECDC4",
													},
													"&.Mui-focused fieldset": {
														borderColor: "#4ECDC4",
													},
												},
											}}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															onClick={() => setShowPassword(!showPassword)}
															edge="end">
															{showPassword ? (
																<VisibilityOff />
															) : (
																<Visibility />
															)}
														</IconButton>
													</InputAdornment>
												),
											}}
										/>

										{/* Forgot Password Link - Sign In Only */}
										{tab === 0 && (
											<Box
												sx={{
													display: "flex",
													justifyContent: "flex-end",
													mt: 1,
												}}>
												<Link
													component="button"
													type="button"
													variant="body2"
													onClick={handleForgotPasswordOpen}
													sx={{
														color: "#4ECDC4",
														textDecoration: "none",
														"&:hover": {
															textDecoration: "underline",
														},
													}}>
													Forgot password?
												</Link>
											</Box>
										)}

										{/* Confirm Password Field - Sign Up Only */}
										{tab === 1 && (
											<Slide direction="left" in={tab === 1} timeout={500}>
												<TextField
													margin="normal"
													required
													fullWidth
													name="confirmPassword"
													label="Confirm password"
													type={showConfirmPassword ? "text" : "password"}
													id="confirmPassword"
													value={formData.confirmPassword}
													onChange={handleChange}
													sx={{
														"& .MuiOutlinedInput-root": {
															borderRadius: 2,
															"& fieldset": {
																borderColor: "#e2e8f0",
															},
															"&:hover fieldset": {
																borderColor: "#4ECDC4",
															},
															"&.Mui-focused fieldset": {
																borderColor: "#4ECDC4",
															},
														},
													}}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<IconButton
																	onClick={() =>
																		setShowConfirmPassword(!showConfirmPassword)
																	}
																	edge="end">
																	{showConfirmPassword ? (
																		<VisibilityOff />
																	) : (
																		<Visibility />
																	)}
																</IconButton>
															</InputAdornment>
														),
													}}
												/>
											</Slide>
										)}

										{/* Submit Button */}
										<Button
											type="submit"
											fullWidth
											variant="contained"
											size="large"
											disabled={loading}
											sx={{
												mt: 3,
												py: 1.5,
												borderRadius: 2,
												textTransform: "none",
												fontSize: "1rem",
												fontWeight: "600",
												backgroundColor: "#1a202c",
												"&:hover": {
													backgroundColor: "#2d3748",
												},
												"&:disabled": {
													background: "rgba(0,0,0,0.12)",
												},
											}}>
											{loading ? (
												<CircularProgress size={24} color="inherit" />
											) : tab === 0 ? (
												"Sign in"
											) : (
												"Create account"
											)}
										</Button>
									</Box>
								</Box>
							</Grid>

							{/* Right Side - Image/Illustration (Hidden on mobile) */}
							{!isMobile && (
								<Grid item md={6}>
									<Box
										sx={{
											height: "100%",
											background:
												"linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											position: "relative",
											overflow: "hidden",
										}}>
										{/* Geometric patterns */}
										<Box
											sx={{
												position: "absolute",
												top: 0,
												left: 0,
												right: 0,
												bottom: 0,
												background: `
													radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
													radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
													radial-gradient(circle at 40% 40%, rgba(255,255,255,0.05) 0%, transparent 50%)
												`,
											}}
										/>

										{/* Content */}
										<Box
											sx={{
												textAlign: "center",
												color: "white",
												zIndex: 1,
												px: 4,
											}}>
											<Box
												sx={{
													mb: 4,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
												}}>
												<img
													src="/miyo_transparent.png"
													alt="Miyo Character"
													style={{
														maxWidth: "300px",
														maxHeight: "300px",
														width: "auto",
														height: "auto",
														objectFit: "contain",
													}}
												/>
											</Box>
											<Typography
												variant="body1"
												sx={{
													opacity: 0.9,
													maxWidth: 350,
													mx: "auto",
													fontSize: "1.1rem",
												}}>
												Join thousands of educators making learning fun and
												engaging for students everywhere.
											</Typography>
										</Box>
									</Box>
								</Grid>
							)}
						</Grid>
					</Paper>
				</Fade>
			</Container>

			{/* Forgot Password Dialog */}
			<Dialog
				open={forgotPasswordOpen}
				onClose={handleForgotPasswordClose}
				PaperProps={{
					sx: {
						borderRadius: 3,
						minWidth: { xs: 300, sm: 400 },
						maxWidth: 500,
					},
				}}>
				<DialogTitle
					sx={{
						textAlign: "center",
						pb: 1,
						background: "linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)",
						color: "white",
						mb: 2,
					}}>
					<Lock sx={{ fontSize: 40, mb: 1 }} />
					<Typography variant="h6" fontWeight="bold">
						Reset Your Password
					</Typography>
				</DialogTitle>
				<DialogContent sx={{ pt: 2, pb: 1, px: 3 }}>
					{!resetEmailSent ? (
						<>
							<DialogContentText
								sx={{ mb: 3, textAlign: "center", color: "text.secondary" }}>
								Enter your email address and we'll send you a secure link to
								reset your password.
							</DialogContentText>
							<TextField
								autoFocus
								margin="dense"
								id="resetEmail"
								label="Email Address"
								type="email"
								fullWidth
								variant="outlined"
								value={resetEmail}
								onChange={(e) => {
									setResetEmail(e.target.value);
									setResetEmailError(""); // Clear error when typing
								}}
								error={!!resetEmailError}
								helperText={resetEmailError}
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: 2,
										"& fieldset": {
											borderColor: "#e2e8f0",
										},
										"&:hover fieldset": {
											borderColor: "#4ECDC4",
										},
										"&.Mui-focused fieldset": {
											borderColor: "#4ECDC4",
										},
									},
								}}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Email sx={{ color: "#4ECDC4" }} />
										</InputAdornment>
									),
								}}
							/>
						</>
					) : (
						<Box sx={{ textAlign: "center", py: 2 }}>
							<Box
								sx={{
									width: 80,
									height: 80,
									borderRadius: "50%",
									background:
										"linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									mx: "auto",
									mb: 2,
								}}>
								<Email
									sx={{
										fontSize: 40,
										color: "white",
									}}
								/>
							</Box>
							<Typography variant="h6" gutterBottom fontWeight="600">
								Check Your Email
							</Typography>
							<DialogContentText sx={{ color: "text.secondary" }}>
								We've sent a password reset link to{" "}
								<strong>{resetEmail}</strong>. Click the link in the email to
								reset your password.
							</DialogContentText>
							<Typography
								variant="body2"
								sx={{ mt: 2, color: "text.secondary" }}>
								Didn't receive the email? Check your spam folder or try again
								with a different email address.
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ p: 3, pt: 1 }}>
					<Button
						onClick={handleForgotPasswordClose}
						sx={{
							borderRadius: 2,
							textTransform: "none",
							color: "text.secondary",
						}}>
						{resetEmailSent ? "Close" : "Cancel"}
					</Button>
					{!resetEmailSent && (
						<Button
							onClick={handleResetPassword}
							disabled={resetLoading || !resetEmail}
							variant="contained"
							sx={{
								borderRadius: 2,
								textTransform: "none",
								backgroundColor: "#1a202c",
								px: 3,
								"&:hover": {
									backgroundColor: "#2d3748",
								},
								"&:disabled": {
									background: "rgba(0,0,0,0.12)",
								},
							}}>
							{resetLoading ? (
								<CircularProgress size={20} color="inherit" />
							) : (
								"Send Reset Link"
							)}
						</Button>
					)}
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default Auth;
